import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useResizeDetector } from 'react-resize-detector'
import { useTranslation } from 'react-i18next'
import { Allotment } from 'allotment'

import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'

import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import Sidebar from '@/layouts/diagnostic/Sidebar'

import { OfflineDocuments } from '@/features/offline/documents'
import OfflineAllDiagnostics from './OfflineAllDiagnostics'
import { consultationOfflineStore, patientOfflineStore } from '@/core/offline'
import { useOfflineMode } from '@/providers/OfflineModeProvider'
import OfflinePatientInfo from './OfflinePatientInfo'

type OfflineLocalConsultationPageProps = {}

export default function OfflineLocalConsultationPage(props: OfflineLocalConsultationPageProps) {
  const navigate = useNavigate()
  const { topbarContentContainer } = useDiagnosticLayout()
  const { consultationId } = useParams()
  const { t } = useTranslation()
  const { width: splitterWrapperWidth = 0, ref: splitterWrapperRef } = useResizeDetector()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })

  const { isOfflineDatabaseReady } = useOfflineMode()

  const [consultation, setConsultation] = useState<any>(null)
  const [patient, setPatient] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  // patient Sidebar
  const patientMenuItems = useMemo(
    () => [
      {
        id: 'patientInfo',
        icon: PatientIdentityIcon,
        label: t('page.localConsultation.sidebar.patientInfo.label'),
        isNested: false,
      },
      {
        id: 'documents',
        icon: ConsultationDocumentsIcon,
        label: t('page.localConsultation.sidebar.documents.label'),
        isNested: false,
      },
    ],
    [t]
  )

  const [patientSelectedMenuItem, setPatientSelectedMenuItem] = useState<string>(patientMenuItems[0].id)

  useEffect(() => {
    const loadConsultationData = async () => {
      if (!consultationId || !isOfflineDatabaseReady) return

      try {
        setLoading(true)

        // Load consultation from offline store
        const offlineConsultation = await consultationOfflineStore.get(consultationId)
        if (!offlineConsultation) {
          console.error('Consultation not found:', consultationId)
          navigate('/offline/patients')
          return
        }

        setConsultation(offlineConsultation)

        // Load patient data - handle both string and object patient references
        let patientId: string
        if (typeof offlineConsultation.patient === 'string') {
          patientId = offlineConsultation.patient
        } else {
          patientId = offlineConsultation.patient._id
        }

        const offlinePatient = await patientOfflineStore.get(patientId)
        if (offlinePatient) {
          setPatient(offlinePatient)
        }
      } catch (error) {
        console.error('Error loading consultation data:', error)
        navigate('/offline/patients')
      } finally {
        setLoading(false)
      }
    }

    loadConsultationData()
  }, [consultationId, isOfflineDatabaseReady, navigate])

  const handleCloseConsultation = useCallback(() => {
    navigate('/offline/patients')
  }, [navigate])

  const handleSelectPatientMenuItem = useCallback((id: string) => {
    setPatientSelectedMenuItem(id)
  }, [])

  const handleConsultationUpdate = useCallback((updatedConsultation: any) => {
    setConsultation(updatedConsultation)
  }, [])

  const handlePatientUpdate = useCallback((updatedPatient: any) => {
    setPatient(updatedPatient)
  }, [])

  // Remove memoization to prevent component re-mounting on consultation updates

  if (!isOfflineDatabaseReady || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Loading consultation...</Typography>
      </Box>
    )
  }

  if (!consultation || !patient) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Consultation not found</Typography>
      </Box>
    )
  }

  return (
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <Portal container={topbarContentContainer}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <Stack direction="column">
              <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '16px', fontWeight: 500 }}>
                {t(`page.localConsultation.title`)} - {patient.userInfo?.name} {patient.userInfo?.surname}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
            <Button
              color="error"
              onClick={handleCloseConsultation}
              size="small"
              variant="contained"
              sx={{
                borderRadius: '17px',
                color: '#FFF',
                fontSize: '14px',
                lineHeight: '16px',
                padding: '10px 15px',
              }}
            >
              {t(`page.localConsultation.topbar.quitConsultationButton.label`)}
            </Button>
          </Stack>
        </Stack>
      </Portal>

      {isMobileScreen ? (
        <Box sx={{ flex: 1, height: '100%', overflowY: 'auto', pt: '20px', pb: '20px' }}>
          <Container maxWidth="md">
            {patientSelectedMenuItem === 'patientInfo' && (
              <OfflinePatientInfo consultation={consultation} patient={patient} onPatientUpdate={handlePatientUpdate} />
            )}
            {patientSelectedMenuItem === 'documents' && <OfflineDocuments consultation={consultation} onConsultationUpdate={handleConsultationUpdate} />}
          </Container>
        </Box>
      ) : (
        <>
          <Box ref={splitterWrapperRef} sx={{ flex: 1, height: '100%' }}>
            <Allotment defaultSizes={[60, 40]}>
              <Allotment.Pane minSize={splitterWrapperWidth / 2.5}>
                <Box sx={{ height: '100%', overflowY: 'auto', pt: '20px', pb: '20px' }}>
                  <Container maxWidth="md">
                    <OfflineAllDiagnostics consultation={consultation} onConsultationUpdate={handleConsultationUpdate} />
                  </Container>
                </Box>
              </Allotment.Pane>
              <Allotment.Pane minSize={splitterWrapperWidth / 3}>
                <Box sx={{ height: '100%', overflowY: 'auto', pt: '20px', pb: '20px' }}>
                  <Container maxWidth="md">
                    {patientSelectedMenuItem === 'patientInfo' && (
                      <OfflinePatientInfo consultation={consultation} patient={patient} onPatientUpdate={handlePatientUpdate} />
                    )}
                    {patientSelectedMenuItem === 'documents' && (
                      <OfflineDocuments consultation={consultation} onConsultationUpdate={handleConsultationUpdate} />
                    )}
                  </Container>
                </Box>
              </Allotment.Pane>
            </Allotment>
          </Box>
          <Sidebar
            id="patient-sidebar"
            menuItems={patientMenuItems}
            onSelect={handleSelectPatientMenuItem}
            selectedMenuItem={patientSelectedMenuItem}
            anchor="right"
          />
        </>
      )}
    </Stack>
  )
}
